import React, { useEffect, useState } from "react";
import {
  FileText,
  User as UserIcon,
  Calendar,
  Trash2,
  Eye,
} from "lucide-react";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";

/* ===============================
   Resume Repository
================================ */

const ResumeRepo = ({ user, builderData, trigger, db, appId }) => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchResumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, appId, trigger]);

  const fetchResumes = async () => {
    if (!user?.uid || !appId) return;

    try {
      setLoading(true);

      const q = query(
        collection(db, "resumes"),
        where("userId", "==", user.uid),
        where("appId", "==", appId),
        orderBy("updatedAt", "desc")
      );

      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setResumes(list);
    } catch (err) {
      console.error(err);
      setError("Failed to load resumes");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!alert("Delete this resume permanently?")) return;
    try {
      await deleteDoc(doc(db, "resumes", id));
      setResumes((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete resume");
    }
  };

  const handleOpen = (resume) => {
    // hydrate builder data
    if (typeof builderData === "function") {
      builderData(resume.data);
    }
  };

  return (
    <div>
      {/* Header */}
      <header className="mb-8">
        <h2 className="text-3xl font-bold text-white">
          Resume Repository
        </h2>
        <p className="text-gray-400 mt-1">
          All your saved resumes
        </p>
      </header>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-300">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-gray-400 text-center py-16">
          Loading resumes…
        </div>
      )}

      {/* Empty */}
      {!loading && resumes.length === 0 && (
        <div className="text-center py-20 bg-gray-900 border border-gray-700 rounded-xl">
          <FileText size={40} className="mx-auto text-gray-500 mb-4" />
          <h3 className="text-lg font-semibold text-white">
            No resumes found
          </h3>
          <p className="text-gray-400 mt-1">
            Create one using the AI Resume Builder
          </p>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {resumes.map((resume) => {
          const info = resume.data?.personalInfo || {};
          const expCount = (resume.data?.experience || []).length;

          return (
            <div
              key={resume.id}
              className="bg-gray-900 border border-gray-700 rounded-xl p-5 hover:border-[#FFD600]/40 transition"
            >
              {/* Top */}
              <div className="flex items-start space-x-3">
                <div className="bg-gray-800 text-[#FFD600] p-3 rounded-full">
                  <UserIcon size={18} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white leading-tight">
                    {info.name || "Untitled Resume"}
                  </h3>
                  <p className="text-sm text-gray-400">
                    {info.email || "—"}
                  </p>
                </div>
              </div>

              {/* Meta */}
              <div className="mt-4 flex items-center text-sm text-gray-400 space-x-4">
                <div className="flex items-center space-x-1">
                  <Calendar size={14} />
                  <span>
                    {resume.updatedAt?.toDate
                      ? resume.updatedAt.toDate().toLocaleDateString()
                      : "—"}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <FileText size={14} />
                  <span>{expCount} roles</span>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={() => handleOpen(resume)}
                  className="flex items-center space-x-2 text-sm font-semibold text-[#FFD600] hover:underline"
                >
                  <Eye size={16} />
                  <span>Open</span>
                </button>

                <button
                  onClick={() => handleDelete(resume.id)}
                  className="text-gray-500 hover:text-red-500"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResumeRepo;
